import { Component, OnDestroy, OnInit } from '@angular/core';
import { RacesService } from '../races/races.service';
import { RaceStandingsService } from '../standings/race-standings.service';
import { combineLatest, Subscription, from, Observable } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Driver } from '../../gepard/drivers/driver';
import { SeasonStanding } from '../standings/season-standing';
import { SeasonPointsPipe } from '../season-points.pipe';
import { SeasonService } from '../season.service';
import { Race } from '../race/race';
import { Ranking } from '../rankings/ranking';
import { RaceService } from '../race/race.service';
import { RaceRanking } from '../race/race-ranking/race-ranking';
import { SeasonRanking } from '../season-rankings/season-ranking';
import { RaceStanding } from '../race/race-standing';
import { ActivatedRoute, Router } from '@angular/router';
import { RankingsService } from '../rankings/rankings.service';
import { AppService } from '../../app.service';

@Component({
  selector: 'mc-season-ranking-standings',
  templateUrl: './season-ranking-standings.component.html',
  styleUrls: ['./season-ranking-standings.component.scss']
})
export class SeasonRankingStandingsComponent implements OnInit, OnDestroy {

  loading = true;

  public readonly season$ = this.seasonService.activeSeason$;

  public readonly rankings$ = this.rankingsService.rankings$.pipe(
    map((rankings) => rankings.filter((ranking) => !ranking.excludeFromSeasonStandings)),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  )

  // Ranking from active route or null.
  // Redirects to the general ranking or the first available ranking as a fallback.
  public readonly ranking$: Observable<Ranking> = this.activatedRoute.data.pipe(
    switchMap((data) => data.ranking$ as Observable<Ranking>),
    tap((ranking) => {
      if (!!ranking.excludeFromSeasonStandings) {
        throw new Error('Ranking excluded from season standings.');
      }
    }),
    catchError((error, caught) => this.rankings$.pipe(
      map((rankings) => rankings[0]),
      map((firstRanking) => this.router.navigate(
        firstRanking ? ['../../', firstRanking.id, 'standings'] : ['../../'],
        {
          replaceUrl: true,
          relativeTo: this.activatedRoute
        })),
      delay(0),
      switchMap(() => caught)
    )),
    tap(() => this.loading = true),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  );

  // public readonly race$s$: Observable<Observable<Race>[]> = this.racesService.racePlans$$.pipe(
  //   map((racePlan$s) => racePlan$s.map((racePlan$) => racePlan$.pipe(
  //     // filter((racePlan) => racePlan.seasonPointsMultiplier > 0),
  //     map((racePlan) => this.raceService.createRace(racePlan))
  //   ))),
  //   shareReplay({
  //     refCount: true,
  //     bufferSize: 1
  //   })
  // );

  public readonly race$sRecord$: Observable<Record<string | number, Observable<Race>>> = this.racesService.racePlans$$.pipe(
    map((racePlan$s) => {
      return racePlan$s.reduce((record, racePlan$) => {
        record[racePlan$._id] = racePlan$.pipe(
          // filter((racePlan) => racePlan.seasonPointsMultiplier > 0),
          map((racePlan) => this.raceService.createRace(racePlan))
        );
        return record;
      }, {});
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  );

  seasonRanking$: Observable<SeasonRanking> = combineLatest([
    this.race$sRecord$.pipe(
      switchMap((race$sRecord) => combineLatest(Object.values(race$sRecord))),
      map((races) => races.filter((race) => !!(race.racePlan.resultsJsonFile || race.racePlan.resultsCouchDbName)))
    ),
    this.ranking$,
    this.season$
  ]).pipe(
    map(([races, ranking, season]): SeasonRanking => {
      const raceRankings = races
        .filter((race) => race.racePlan.seasonPointsMultiplier > 0)
        .map<RaceRanking>((race) => this.raceStandingsService.createRaceRanking(race, ranking));

      const maxRacesToSeasonRanking = ranking.maxRacesToSeasonRanking
        || season.maxRacesToSeasonRanking
        || ((raceRankings.length > 1) ? raceRankings.length - 1 : 1);

      const byDriver: Record<number, SeasonStanding> = {};
      let i = 0;

      const seasonStandings$: Observable<SeasonStanding[]> = from(raceRankings).pipe(
        mergeMap((raceRanking) => {
          return raceRanking.standings$.pipe(
            map((raceStandings) => {
              raceStandings.forEach((raceStanding: RaceStanding) => {
                if (!(raceStanding.driver.startNumber in byDriver)) {
                  byDriver[raceStanding.driver.startNumber] = this.createSeasonStanding(raceStanding.driver);
                }
                byDriver[raceStanding.driver.startNumber].raceStandings[raceRanking.race.racePlan._id] = raceStanding;
                byDriver[raceStanding.driver.startNumber].raceStandings[raceRanking.race.racePlan._id].seasonPoints = this.seasonPointsPipe.transform(
                  raceStanding.position,
                  season.pointsMap,
                  raceRanking.race.racePlan.seasonPointsMultiplier,
                  raceStanding.hasRaceBastLap
                );
              });

              const seasonStandings: SeasonStanding[] = Object.values(byDriver);

              seasonStandings.forEach((seasonStanding: SeasonStanding) => {
                seasonStanding.bestRaceStandings = Object.values(seasonStanding.raceStandings)
                  .sort((a, b) => a.seasonPoints - b.seasonPoints)
                  .slice(-1 * maxRacesToSeasonRanking);

                seasonStanding.points = 0;
                seasonStanding.bestRaceStandings.forEach((raceStanding) => {
                  seasonStanding.points += raceStanding.seasonPoints;
                });

                seasonStanding.racePositionsHash = Object.values(seasonStanding.raceStandings)
                  .map((raceStanding) => raceStanding.position)
                  .sort((a: number, b: number) => a - b)
                  .map(racePosition => ('00' + racePosition).slice(-3))
                  .join('');
              });

              seasonStandings
                .sort((a, b) => {
                  if (a.points !== b.points) {
                    return b.points - a.points;
                  } else if (a.racePositionsHash < b.racePositionsHash) {
                    return -1;
                  } else if (a.racePositionsHash > b.racePositionsHash) {
                    return 1;
                  } else {
                    return 0;
                  }
                });

              let position = 0;
              let previousDriverStanding;
              seasonStandings.forEach(driverStanding => {
                if (previousDriverStanding) {
                  if (previousDriverStanding.points !== driverStanding.points) {
                    position++;
                  } else if (previousDriverStanding.racePositionsHash !== driverStanding.racePositionsHash) {
                    position++;
                  }
                } else {
                  position++;
                }
                driverStanding.position = position;
                previousDriverStanding = driverStanding;
              });

              return seasonStandings;
            })
          )
        }),
        tap(() => {
          if (++i === raceRankings.length) {
            this.loading = false;
          }
        })
      );

      return {
        season,
        ranking: raceRankings[0].ranking,
        raceRankings,
        standings$: seasonStandings$
      };
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  );

  private initSubscription: Subscription;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly appService: AppService,
    private readonly seasonPointsPipe: SeasonPointsPipe,
    private readonly seasonService: SeasonService,
    private readonly racesService: RacesService,
    private readonly raceService: RaceService,
    private readonly raceStandingsService: RaceStandingsService,
    private readonly rankingsService: RankingsService
  ) {
  }

  ngOnInit() {
    this.loading = true;
    // this.initSubscription = combineLatest([
    //   this.season$,
    //   this.seasonRanking$
    // ]).subscribe(([season, raceRankings]) => {
    //
    //   this.rankings = season.rankings.map(ranking => {
    //     const driversStandingsByStartNumber = {};
    //     raceRankings.forEach(raceRanking => {
    //
    //       raceRanking.standings$.forEach((raceStanding) => {
    //         if (!driversStandingsByStartNumber[raceStanding.driver.startNumber]) {
    //           driversStandingsByStartNumber[raceStanding.driver.startNumber] = {
    //             name: raceStanding.driver.name,
    //             startNumber: raceStanding.driver.startNumber,
    //             points: {},
    //             racePositions: {},
    //             seasonPoints: 0
    //           };
    //         }
    //       });
    //
    //       this.standingsService.recalculateStandingsBestLap(rankingStandings);
    //
    //       rankingStandings.forEach(standing => {
    //         driversStandingsByStartNumber[standing.driver.startNumber].racePositions[raceRanking.race._id] = standing.position;
    //         driversStandingsByStartNumber[standing.driver.startNumber].points[raceRanking.race._id] = this.seasonPointsPipe
    //           .transform(standing.position, raceRanking.race.seasonPointsMultiplier, standing.hasRaceBastLap);
    //       });
    //     });
    //
    //     for (const i in driversStandingsByStartNumber) {
    //       if (driversStandingsByStartNumber.hasOwnProperty(i) && driversStandingsByStartNumber[i].points) {
    //         const racesPoints = Object.values(driversStandingsByStartNumber[i].points);
    //         racesPoints.sort((a: number, b: number) => a - b);
    //         racesPoints.slice(-1 * (ranking.maxRacesToSeasonRanking || season.maxRacesToSeasonRanking))
    //           .forEach(points => driversStandingsByStartNumber[i].points += points);
    //         driversStandingsByStartNumber[i].racePositionsHash = Object.values(driversStandingsByStartNumber[i].racePositions)
    //           .sort((a: number, b: number) => a - b)
    //           .map(racePosition => ('00' + racePosition).slice(-3))
    //           .join('');
    //       }
    //     }
    //
    //     const driversStandings = Object.values<{
    //         points: { number },
    //         racePositions: { number },
    //         racePositionsHash: number[],
    //         seasonPoints: number,
    //         position: number
    //       }>(driversStandingsByStartNumber)
    //       .sort((a, b) => {
    //         if (a.seasonPoints !== b.seasonPoints) {
    //           return b.seasonPoints - a.seasonPoints;
    //         } else if (a.racePositionsHash < b.racePositionsHash) {
    //           return -1;
    //         } else if (a.racePositionsHash > b.racePositionsHash) {
    //           return 1;
    //         } else {
    //           return 0;
    //         }
    //       });
    //
    //     let position = 0;
    //     let previousDriverStanding;
    //     driversStandings.forEach(driverStanding => {
    //       if (previousDriverStanding) {
    //         if (previousDriverStanding.points !== driverStanding.seasonPoints) {
    //           position++;
    //         } else if (previousDriverStanding.racePositionsHash !== driverStanding.racePositionsHash) {
    //           position++;
    //         }
    //       } else {
    //         position++;
    //       }
    //       driverStanding.position = position;
    //       previousDriverStanding = driverStanding;
    //     });
    //
    //     return {
    //       driversStandings,
    //       ranking
    //     };
    //   });
    //
    //   this.loading = false;
    //   this.appService.fixView();
    // });
  }

  ngOnDestroy() {
    if (this.initSubscription) {
      this.initSubscription.unsubscribe();
    }
  }

  createSeasonStanding(driver: Driver): SeasonStanding {
    return {
      driver,
      raceStandings: {},
      bestRaceStandings: [],
      position: 0,
      points: 0
    };
  }

  // recalculate(rankingStandings: SeasonStanding[], resultsConfig: ResultsConfig) {
  //   rankingStandings
  //     .forEach(standing => {
  //       standing.bestResults = this.recalculateResults(standing.results, resultsConfig);
  //       standing.bestResultsAverage = this.average(standing.bestResults.map(result => result.microtime));
  //     });
  //   return this.recalculateRanking(rankingStandings);
  //   // this.rankingStandings.forEach(s => s.results.forEach(r => this.resultsMetaService.get(r)));
  //   // this.resultsMetaService.recalculateDuplicates();
  // }
  //
  // recalculateResults(results: Result[], resultsConfig: ResultsConfig): Result[] {
  //   return this.resultsFilter.transform(results, resultsConfig)
  //     .sort((a, b) => a.microtime - b.microtime)
  //     .slice(0, resultsConfig.bestLapsNumber);
  // }
  //
  // recalculateRanking(rankingStandings: SeasonStanding[]) {
  //   rankingStandings.sort((a, b) => a.bestResults.length <= 0 ? 1 : a.bestResultsAverage - b.bestResultsAverage);
  //   rankingStandings.forEach((standing, i) => {
  //     standing.position = i + 1;
  //   });
  //   return rankingStandings;
  // }
  //
  // average(results, n = 3) {
  //   let i;
  //   let sum = 0;
  //   for (i = 0; i < results.length && (n <= 0 || i < n); i++) {
  //     sum += results[i];
  //   }
  //   return i ? (sum / i) : null;
  // }
}
